import { ModalProvider } from "../../../Modal/Modal";
import {
  TableBody,
  TableHead,
  TableRoot,
  TableRow,
} from "../../../Table/TableBasic";
import OpretNyButtonBlock from "../../person/id/OpretNyButtonBlock";
import LabelIcon from "../../../icons/LabelIcon";
import { useQuery } from "@tanstack/react-query";
import { get } from "../../../../api/util";
import Spinner from "../../../spinner/Spinner";
import { SortBy } from "../../../../interfaces/table";
import useLocalStorageState from "use-local-storage-state";
import {
  SortableTH,
  Sortings,
  TD,
  getHeaderActiveColumns,
} from "../../../../util/table";
import useActiveColumns from "../../../../hooks/useActiveColumns";
import { stringDateToPP } from "../../../../util/date";
import { defaultPrissaetningColumns } from "../../../../config/admin/columnsConfig";
import { adminPrismatrixConfig } from "../../../../config/form/admin/adminPrismatrixConfig";
import ActiveIcon from "../../../icons/ActiveIcon";
import IngenRegistreringer from "../../person/id/IngenRegistreringer";
import { useContext } from "react";
import { UserQueryContext } from "../../../../context/UserContext";
import { PrismatrixDTO } from "../../../../interfaces/formDto";
import { differenceInCalendarDays } from "date-fns";
import { orderBy } from "../../../../util/orderBy";

export default function Prissaetning() {
  const { user, hasPermission } = useContext(UserQueryContext);
  const canWrite = hasPermission(user, "AdminWrite");
  const [sortBy, setSortBy] = useLocalStorageState<SortBy[]>(
    "adminPrissaetningSortBy",
    {
      defaultValue: defaultSortBy,
    },
  );
  const [columns] = useActiveColumns("", defaultPrissaetningColumns, false);
  const { data, isLoading, error, refetch } = useQuery<PrismatrixDTO[]>({
    queryKey: ["prismatrix"],
    queryFn: async function () {
      return get({
        query: "prismatrix",
      });
    },
  });
  if (error) return <div>Fejl</div>;
  if (isLoading) return <Spinner />;
  if (!data) return null;

  if (data.length === 0) {
    return (
      <IngenRegistreringer
        editConfig={canWrite ? adminPrismatrixConfig() : undefined}
        invalidateData={refetch}
      />
    );
  }

  const prismatrix = data.map((e) => {
    const fraDato = new Date(e.periodeFraDato);
    const tilDatoInklusiv = new Date(e.periodeTilDatoInklusiv ?? "9999-12-31");
    const current = new Date();
    return { ...e, active: current >= fraDato && current < tilDatoInklusiv };
  });
  const activePrissaetning = prismatrix.find((e) => e.active);
  const openTilDate = prismatrix.find((e) =>
    e.periodeTilDatoInklusiv?.startsWith("9999"),
  );
  const latestDateEntry = prismatrix
    .filter(
      (e) =>
        e.periodeTilDatoInklusiv &&
        !e.periodeTilDatoInklusiv.startsWith("9999"),
    )
    .sort(
      (a, b) =>
        new Date(b.periodeTilDatoInklusiv as string).getTime() -
        new Date(a.periodeTilDatoInklusiv as string).getTime(),
    )
    .shift();

  const sortings: Sortings = {
    defaultSortBy,
    setSortBy,
    sortBy,
  };

  return (
    <div className="container">
      <h3 className="d-flex gap-2 align-items-center">
        <span className="c-theme-icon c-theme-icon--defaultSize text-primary bg-primary-light">
          <LabelIcon width={20} />
        </span>
        <span>Prissætning</span>
      </h3>
      <div className="c-accordion">
        <ModalProvider>
          <TableRoot>
            <TableHead>
              <>
                <th className="indicator" />
                <SortableTH
                  field={"periodeFraDato"}
                  sortings={sortings}
                  title="Gældende fra - til"
                />
                {getHeaderActiveColumns(columns, sortings, { color: "black" })}
              </>
            </TableHead>
            <TableBody>
              {orderBy(
                prismatrix,
                sortBy.map((s) => s.field),
                sortBy.map((s) => s.direction),
              ).map((rowData, i) => {
                const today = new Date();
                const isEditable =
                  new Date(rowData.periodeFraDato) > today ||
                  differenceInCalendarDays(
                    new Date(rowData.periodeTilDatoInklusiv ?? "9999-12-31"),
                    today,
                  ) >= 0;
                return (
                  <TableRow
                    key={i}
                    editConfig={
                      isEditable && canWrite
                        ? adminPrismatrixConfig(
                            rowData.id,
                            undefined,
                            latestDateEntry?.id,
                          )
                        : undefined
                    }
                    invalidateData={refetch}
                  >
                    <>
                      <td
                        className={`indicator${
                          rowData.active ? " has-effect" : ""
                        }`}
                      >
                        <ActiveIcon />
                      </td>
                      <TD
                        col={{
                          active: true,
                          field: "Gaeldende",
                          format: "string",
                          title: "Gældende fra - til",
                        }}
                        rowData={{
                          Gaeldende:
                            stringDateToPP(rowData.periodeFraDato.toString()) +
                            (rowData.periodeTilDatoInklusiv &&
                            rowData.periodeTilDatoInklusiv?.split("-")[0] !==
                              "9999"
                              ? " - "
                              : "") +
                            (rowData.periodeTilDatoInklusiv
                              ? stringDateToPP(
                                  rowData.periodeTilDatoInklusiv.toString(),
                                  "",
                                )
                              : ""),
                        }}
                      />
                      {columns
                        .filter((col) => col.active)
                        .map((col) => {
                          return (
                            <TD key={col.field} col={col} rowData={rowData} />
                          );
                        })}
                    </>
                  </TableRow>
                );
              })}
              <tr />
            </TableBody>
          </TableRoot>
          <OpretNyButtonBlock
            invalidateData={refetch}
            editConfig={
              canWrite
                ? adminPrismatrixConfig(activePrissaetning?.id, openTilDate?.id)
                : undefined
            }
          />
        </ModalProvider>
      </div>
    </div>
  );
}

const defaultSortBy: SortBy[] = [
  {
    direction: "desc",
    field: "periodeFraDato",
  },
];
